import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { formatApiError } from "../lib/api";
import Header from "../components/Header";
import Logo from "../components/Logo";
import PasswordChecklist, { passwordIsValid } from "../components/PasswordChecklist";
import { ArrowRight } from "lucide-react";

const INTENT_KEY = "cafespot_intent_plan";

export default function Register() {
  const { register } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const intentPlan = searchParams.get("plan");
  const intentRole = searchParams.get("role");
  const [role, setRole] = useState(intentRole === "cafe_owner" ? "cafe_owner" : "customer");
  const [form, setForm] = useState({ email: "", password: "", name: "", cafe_name: "" });
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Persist the chosen plan so the Billing page can auto-trigger checkout after verification
  useEffect(() => {
    if (intentPlan === "standard" || intentPlan === "premium") {
      localStorage.setItem(INTENT_KEY, intentPlan);
    }
  }, [intentPlan]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pwdValid = passwordIsValid(form.password);
  const pwdMatch = confirm.length > 0 && confirm === form.password;
  const formReady = pwdValid && pwdMatch && form.email && form.name && (role !== "cafe_owner" || form.cafe_name);

  const submit = async (e) => {
    e.preventDefault();
    if (!formReady) return;
    setLoading(true);
    setErr("");
    try {
      const payload = { email: form.email, password: form.password, name: form.name, role };
      if (role === "cafe_owner") payload.cafe_name = form.cafe_name;
      const user = await register(payload);
      nav(user.role === "cafe_owner" ? "/dashboard/billing" : "/");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-screen text-[#F5F0EB]">
      <Header />
      <div className="min-h-screen flex items-center justify-center px-6 pt-24 pb-16">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8"><Logo size={64} variant="mark" /></div>
          <div className="overline mb-6 text-center">Rejoindre CAFÉ-SPOT</div>
          <h1 className="font-serif text-5xl font-light mb-3 text-center">{t("auth.createAccount")}</h1>
          <p className="text-[#A8A39D] text-sm mb-10 text-center">{t("auth.hasAccount")} <Link to="/login" className="text-[#C5A059] hover:underline">{t("auth.signIn")}</Link></p>

          {intentPlan && (intentPlan === "standard" || intentPlan === "premium") && (
            <div data-testid="register-intent-banner" className="border border-[#C5A059]/40 bg-[#C5A059]/5 p-4 mb-8 text-center">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059] mb-1">Forfait sélectionné</div>
              <div className="font-serif text-lg text-[#F5F0EB]">
                {intentPlan === "premium" ? "Premium · 59 $ CAD / mois" : "Standard · 39 $ CAD / mois"}
              </div>
              <div className="text-[11px] text-[#A8A39D] mt-1">Créez votre compte pour finaliser l'abonnement</div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 mb-8">
            <button data-testid="role-customer" type="button" onClick={() => setRole("customer")} className={`py-3 text-xs tracking-[0.2em] uppercase border transition-colors ${role === "customer" ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5" : "border-white/10 text-[#A8A39D] hover:text-[#F5F0EB]"}`}>{t("auth.asCustomer")}</button>
            <button data-testid="role-cafe-owner" type="button" onClick={() => setRole("cafe_owner")} className={`py-3 text-xs tracking-[0.2em] uppercase border transition-colors ${role === "cafe_owner" ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5" : "border-white/10 text-[#A8A39D] hover:text-[#F5F0EB]"}`}>{t("auth.asCafeOwner")}</button>
          </div>

          <form onSubmit={submit} className="space-y-6">
            <div>
              <label className="overline text-xs">{t("auth.name")}</label>
              <input data-testid="reg-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="luxe-input mt-2" />
            </div>
            {role === "cafe_owner" && (
              <div>
                <label className="overline text-xs">{t("auth.cafeName")}</label>
                <input data-testid="reg-cafe-name" required value={form.cafe_name} onChange={(e) => set("cafe_name", e.target.value)} className="luxe-input mt-2" />
              </div>
            )}
            <div>
              <label className="overline text-xs">{t("auth.email")}</label>
              <input data-testid="reg-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="luxe-input mt-2" />
            </div>
            <div>
              <label className="overline text-xs">{t("auth.password")}</label>
              <input data-testid="reg-password" type="password" required value={form.password} onChange={(e) => set("password", e.target.value)} className="luxe-input mt-2" autoComplete="new-password" />
            </div>
            <div>
              <label className="overline text-xs">Confirmer le mot de passe</label>
              <input data-testid="reg-password-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="luxe-input mt-2" autoComplete="new-password" />
              <PasswordChecklist password={form.password} confirm={confirm} showConfirmRow lang={lang} />
            </div>
            {err && <div data-testid="reg-error" className="text-red-400 text-sm">{err}</div>}
            <button data-testid="reg-submit" type="submit" disabled={loading || !formReady} className="btn-gold w-full mt-8 disabled:opacity-40 disabled:cursor-not-allowed">
              {loading ? "..." : t("auth.submit")} <ArrowRight size={14} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
